/**
 * Analytics SDK 설정 관리
 */

export interface AnalyticsConfig {
    // API 엔드포인트
    endpoint: string;

    // 버퍼링 설정
    bufMax: number;           // 최대 이벤트 수
    bufMs: number;            // 최대 대기 시간 (ms)
    maxPayloadBytes: number;  // 최대 페이로드 크기 (bytes)

    // 디버그 모드
    debug: boolean;

    // 샘플링 설정
    sampling: {
        impression: number;     // 0.0 ~ 1.0
        minorClick: number;     // 0.0 ~ 1.0
    };

    // 오프라인 큐 설정
    offlineQueue: {
        maxItems: number;       // 최대 저장 이벤트 수
        storageKey: string;     // localStorage 키
    };

    // 세션별 상한
    limits: {
        impressionPerSession: number;
        minorInteractionPerSession: number;
    };

    // 자동 계측 설정
    autoTracking: {
        pageView: boolean;
        pageLeave: boolean;
        impressions: boolean;
        clicks: boolean;
    };
}

// 기본 설정
export const DEFAULT_CONFIG: AnalyticsConfig = {
    endpoint: "/api/track",
    bufMax: 10,
    bufMs: 3000,
    maxPayloadBytes: 60000,
    debug: false,
    sampling: {
        impression: 1.0, // 본 것의 100% 전송
        minorClick: 1.0, // 클릭 이벤트의 100% 전송
    },
    offlineQueue: {
        maxItems: 200,
        storageKey: 'rw_analytics_queue',
    },
    limits: {
        // 🔧 개발 환경에서는 무제한, 프로덕션에서는 제한
        impressionPerSession: process.env.NODE_ENV === 'development' ? Infinity : 300,
        minorInteractionPerSession: process.env.NODE_ENV === 'development' ? Infinity : 50,
    },
    autoTracking: {
        pageView: true,
        pageLeave: true,
        impressions: true,
        clicks: true,
    },
};

// 현재 설정
let currentConfig: AnalyticsConfig = { ...DEFAULT_CONFIG };

/**
 * 설정 초기화
 */
export function initConfig(config: Partial<AnalyticsConfig> = {}): AnalyticsConfig {
    currentConfig = {
        ...DEFAULT_CONFIG,
        ...config,
        sampling: {
            ...DEFAULT_CONFIG.sampling,
            ...config.sampling,
        },
        offlineQueue: {
            ...DEFAULT_CONFIG.offlineQueue,
            ...config.offlineQueue,
        },
        limits: {
            ...DEFAULT_CONFIG.limits,
            ...config.limits,
        },
        autoTracking: {
            ...DEFAULT_CONFIG.autoTracking,
            ...config.autoTracking,
        },
    };

    return currentConfig;
}

/**
 * 현재 설정 반환
 */
export function getConfig(): AnalyticsConfig {
    return currentConfig;
}

/**
 * 설정 업데이트
 */
export function updateConfig(config: Partial<AnalyticsConfig>): AnalyticsConfig {
    currentConfig = {
        ...currentConfig,
        ...config,
        sampling: {
            ...currentConfig.sampling,
            ...config.sampling,
        },
        offlineQueue: {
            ...currentConfig.offlineQueue,
            ...config.offlineQueue,
        },
        limits: {
            ...currentConfig.limits,
            ...config.limits,
        },
        autoTracking: {
            ...currentConfig.autoTracking,
            ...config.autoTracking,
        },
    };

    return currentConfig;
}

/**
 * 샘플링 체크
 */
export function shouldSample(type: keyof AnalyticsConfig['sampling']): boolean {
    const rate = currentConfig.sampling[type];
    return Math.random() < rate; // 0.0 ~ 1.0 사이의 랜덤 값이 rate보다 작으면 샘플링
}

/**
 * 세션별 한도 체크를 위한 카운터 관리
 */
const SESSION_COUNTERS_KEY = 'rw_session_counters';

interface SessionCounters {
    sessionId: string;
    impressions: number;
    minorInteractions: number;
}

function getSessionCounters(): SessionCounters {
    if (typeof localStorage === 'undefined') {
        return { sessionId: '', impressions: 0, minorInteractions: 0 };
    }

    try {
        const stored = localStorage.getItem(SESSION_COUNTERS_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch {
        // 파싱 실패 시 기본값 반환
    }

    return { sessionId: '', impressions: 0, minorInteractions: 0 };
}

function setSessionCounters(counters: SessionCounters): void {
    if (typeof localStorage === 'undefined') return;

    try {
        localStorage.setItem(SESSION_COUNTERS_KEY, JSON.stringify(counters));
    } catch {
        // 저장 실패 시 무시
    }
}

/**
 * 세션별 한도 체크 및 카운터 증가
 */
export function checkAndIncrementLimit(
    type: 'impression' | 'minorInteraction',
    sessionId: string
): boolean {
    const counters = getSessionCounters();

    // 세션이 바뀌면 카운터 초기화
    if (counters.sessionId !== sessionId) {
        counters.sessionId = sessionId;
        counters.impressions = 0;
        counters.minorInteractions = 0;
    }

    const limits = currentConfig.limits;

    if (type === 'impression') {
        if (counters.impressions >= limits.impressionPerSession) {
            return false; // 한도 초과
        }
        counters.impressions++;
    } else {
        if (counters.minorInteractions >= limits.minorInteractionPerSession) {
            return false; // 한도 초과
        }
        counters.minorInteractions++;
    }

    setSessionCounters(counters);
    return true;
}

/**
 * 환경변수 기반 설정 로딩
 */
export function loadConfigFromEnv(): Partial<AnalyticsConfig> {
    if (typeof process === 'undefined' || !process.env) {
        return {};
    }

    const config: Partial<AnalyticsConfig> = {};

    if (process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT) {
        config.endpoint = process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT;
    }

    if (process.env.NEXT_PUBLIC_ANALYTICS_DEBUG) {
        config.debug = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true';
    }

    if (process.env.NEXT_PUBLIC_ANALYTICS_SAMPLING_IMPRESSION) {
        const rate = parseFloat(process.env.NEXT_PUBLIC_ANALYTICS_SAMPLING_IMPRESSION);
        if (!isNaN(rate) && rate >= 0 && rate <= 1) {
            config.sampling = {
                impression: rate,
                minorClick: config.sampling?.minorClick || DEFAULT_CONFIG.sampling.minorClick
            };
        }
    }

    // 환경변수로 세션별 한도 제어 (개발용)
    if (process.env.NEXT_PUBLIC_ANALYTICS_LIMIT_IMPRESSION) {
        const limit = parseInt(process.env.NEXT_PUBLIC_ANALYTICS_LIMIT_IMPRESSION, 10);
        if (!isNaN(limit) && limit >= 0) {
            config.limits = {
                impressionPerSession: limit,
                minorInteractionPerSession: config.limits?.minorInteractionPerSession || DEFAULT_CONFIG.limits.minorInteractionPerSession
            };
        }
    }

    if (process.env.NEXT_PUBLIC_ANALYTICS_LIMIT_INTERACTION) {
        const limit = parseInt(process.env.NEXT_PUBLIC_ANALYTICS_LIMIT_INTERACTION, 10);
        if (!isNaN(limit) && limit >= 0) {
            config.limits = {
                impressionPerSession: config.limits?.impressionPerSession || DEFAULT_CONFIG.limits.impressionPerSession,
                minorInteractionPerSession: limit
            };
        }
    }

    return config;
}
